@if (!loading()) {
  @if (!linked()) {
    @if (entitled()) {
      <div class="alert alert-info small mb-3">{{ 'mycards.partner.not_linked' | translate }}</div>
    }
  } @else {
    <!-- Status -->
    @if (account(); as acc) {
      <c-card class="mb-3">
        <c-card-header class="d-flex flex-wrap align-items-center gap-2">
          <strong class="me-auto">{{ 'mycards.partner.status_title' | translate }}</strong>
          <button cButton color="secondary" variant="outline" size="sm" [disabled]="refreshing()" (click)="refresh()">
            @if (refreshing()) { <c-spinner size="sm" class="me-1"></c-spinner> }
            {{ 'mycards.partner.refresh' | translate }}
          </button>
        </c-card-header>
        <c-card-body>
          <div class="d-flex flex-wrap gap-4 mb-2">
            <div>
              <div class="small text-medium-emphasis">{{ 'mycards.partner.account_status' | translate }}</div>
              <c-badge [color]="accountColor(acc.account_status)">{{ keyOr('account', acc.account_status) | translate }}</c-badge>
            </div>
            <div>
              <div class="small text-medium-emphasis">{{ 'mycards.partner.kyc_status' | translate }}</div>
              <c-badge [color]="kycColor(acc.kyc_status)">{{ keyOr('kyc', acc.kyc_status) | translate }}</c-badge>
            </div>
            <div>
              <div class="small text-medium-emphasis">{{ 'mycards.partner.card_activated' | translate }}</div>
              <div>{{ acc.card_activated_at ? date(acc.card_activated_at) : ('mycards.partner.not_yet' | translate) }}</div>
            </div>
          </div>

          @if (kycActionNeeded(acc.kyc_status)) {
            <div class="alert alert-warning small mb-2">
              {{ 'mycards.partner.kyc_action_hint' | translate }}
              @if (acc.kyc_reason) {
                <div class="mt-1"><strong>{{ 'mycards.partner.kyc_reason' | translate }}:</strong> {{ acc.kyc_reason }}</div>
              }
            </div>
          }

          <div class="small text-medium-emphasis">
            {{ 'mycards.partner.last_updated' | translate }}: {{ relative(acc.details_synced_at || acc.synced_at) }}
          </div>
          @if (refreshHint(); as hint) {
            <div class="small text-warning mt-1">{{ hint | translate }}</div>
          }
        </c-card-body>
      </c-card>
    }

    <!-- Cards -->
    <c-card class="mb-3">
      <c-card-header><strong>{{ 'mycards.partner.cards_title' | translate }}</strong></c-card-header>
      <c-card-body>
        @if (cards().length === 0) {
          <div class="text-medium-emphasis">{{ 'mycards.partner.no_cards' | translate }}</div>
        }
        @for (c of cards(); track c.id; let last = $last) {
          <div class="d-flex flex-wrap align-items-center gap-2" [class.mb-3]="!last">
            <code class="fs-5 me-2">•••• {{ c.last_four || '????' }}</code>
            <c-badge [color]="cardColor(c.card_status)">{{ keyOr('card', c.card_status) | translate }}</c-badge>
            <div class="w-100 small">
              <span class="text-medium-emphasis">{{ 'mycards.partner.physical_card' | translate }}:</span>
              {{ (c.physical_status ? keyOr('physical', c.physical_status) : 'mycards.partner.physical_none') | translate }}
              @if (c.physical_ordered_at) {
                <span class="text-medium-emphasis ms-2">{{ 'mycards.partner.ordered_at' | translate }} {{ date(c.physical_ordered_at) }}</span>
              }
              @if (c.physical_distributed_at) {
                <span class="text-medium-emphasis ms-2">{{ 'mycards.partner.distributed_at' | translate }} {{ date(c.physical_distributed_at) }}</span>
              }
            </div>
          </div>
        }
      </c-card-body>
    </c-card>

    <!-- Deposit addresses -->
    <c-card class="mb-3">
      <c-card-header><strong>{{ 'mycards.partner.addresses_title' | translate }}</strong></c-card-header>
      <c-card-body>
        @if (addressGroups().length === 0) {
          <div class="text-medium-emphasis">{{ 'mycards.partner.no_addresses' | translate }}</div>
        } @else {
          <p class="small mb-2">{{ 'mycards.partner.addresses_intro' | translate }}</p>
          <div class="alert alert-warning small py-2">{{ 'mycards.partner.addresses_warning' | translate }}</div>
          @for (g of addressGroups(); track g.network + g.address) {
            <div class="address-row">
              <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                <strong>{{ g.label }}</strong>
                @for (tok of g.tokens; track tok) {
                  <c-badge color="primary">{{ tok }}</c-badge>
                }
              </div>
              <div class="d-flex align-items-stretch gap-2">
                <code class="address flex-grow-1">{{ g.address }}</code>
                <button cButton color="secondary" variant="outline" size="sm" class="flex-shrink-0" (click)="copy(g.address)">
                  {{ (copied() === g.address ? 'mycards.partner.copied' : 'mycards.partner.copy') | translate }}
                </button>
              </div>
              @if (g.memo) {
                <div class="small mt-1">{{ 'mycards.partner.memo' | translate }}: <code>{{ g.memo }}</code></div>
              }
            </div>
          }
        }
      </c-card-body>
    </c-card>

    <!-- Card transactions -->
    <c-card class="mb-3">
      <c-card-header><strong>{{ 'mycards.partner.tx_title' | translate }}</strong></c-card-header>
      <c-card-body>
        @if (transactions().length === 0) {
          <div class="text-medium-emphasis">{{ 'mycards.partner.no_tx' | translate }}</div>
        } @else {
          <div class="table-scroll">
            <table cTable [small]="true" class="align-middle mb-0">
              <thead>
                <tr>
                  <th>{{ 'mycards.partner.date' | translate }}</th>
                  <th>{{ 'mycards.partner.description' | translate }}</th>
                  <th class="text-end">{{ 'mycards.partner.amount' | translate }}</th>
                  <th>{{ 'mycards.partner.status' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (t of transactions(); track t.id) {
                  <tr [class.text-decoration-line-through]="t.status === 'Fail'">
                    <td class="small text-nowrap">{{ dateTime(t.time) }}</td>
                    <td>
                      {{ txDescription(t) || (keyOr('type', t.type) | translate) }}
                      @if (t.card_last_four) { <code class="small ms-1">••••{{ t.card_last_four }}</code> }
                    </td>
                    <td class="text-end text-nowrap">
                      <span [class.text-success]="txDirection(t) === 'in'" [class.text-danger]="txDirection(t) === 'out'">{{ txAmount(t) }}</span>
                      @if (txOriginal(t); as orig) { <div class="small text-medium-emphasis">{{ orig }}</div> }
                      @if (txFee(t); as fee) { <div class="small text-medium-emphasis">{{ 'mycards.partner.fee' | translate }} {{ fee }}</div> }
                    </td>
                    <td><c-badge [color]="statusColor(t.status)">{{ keyOr('tx_status', t.status) | translate }}</c-badge></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          @if (transactions().length < transactionsTotal()) {
            <button cButton color="secondary" variant="outline" size="sm" class="mt-3"
                    [disabled]="listBusy() === 'transactions'" (click)="loadMore('transactions')">
              @if (listBusy() === 'transactions') { <c-spinner size="sm" class="me-1"></c-spinner> }
              {{ 'mycards.partner.load_more' | translate }} ({{ transactions().length }} / {{ transactionsTotal() }})
            </button>
          }
        }
      </c-card-body>
    </c-card>

    <!-- Crypto deposits + withdrawals share one table layout -->
    @for (section of ['deposits', 'withdrawals']; track section) {
      <c-card class="mb-3">
        <c-card-header><strong>{{ ('mycards.partner.' + section + '_title') | translate }}</strong></c-card-header>
        <c-card-body>
          @let rows = section === 'deposits' ? deposits() : withdrawals();
          @let total = section === 'deposits' ? depositsTotal() : withdrawalsTotal();
          @if (rows.length === 0) {
            <div class="text-medium-emphasis">{{ ('mycards.partner.no_' + section) | translate }}</div>
          } @else {
            <div class="table-scroll">
              <table cTable [small]="true" class="align-middle mb-0">
                <thead>
                  <tr>
                    <th>{{ 'mycards.partner.date' | translate }}</th>
                    <th>{{ 'mycards.partner.network' | translate }}</th>
                    <th class="text-end">{{ 'mycards.partner.amount' | translate }}</th>
                    <th>{{ 'mycards.partner.status' | translate }}</th>
                    <th>{{ 'mycards.partner.tx_hash' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (r of rows; track r.id) {
                    <tr>
                      <td class="small text-nowrap">{{ dateTime(r.time) }}</td>
                      <td class="text-nowrap">{{ chainLabel(r.chain) }} <c-badge color="primary" class="ms-1">{{ r.currency }}</c-badge></td>
                      <td class="text-end text-nowrap">
                        <span [class.text-success]="section === 'deposits'" [class.text-danger]="section === 'withdrawals'">
                          {{ section === 'deposits' ? '+' : '−' }}{{ crypto(r.amount) }} {{ r.currency }}
                        </span>
                        @if (r.fee && +r.fee > 0) {
                          <div class="small text-medium-emphasis">{{ 'mycards.partner.fee' | translate }} {{ crypto(r.fee) }} {{ r.currency }}</div>
                        }
                      </td>
                      <td><c-badge [color]="statusColor(r.status)">{{ keyOr('transfer_status', r.status) | translate }}</c-badge></td>
                      <td class="text-nowrap">
                        @if (r.transaction_hash) {
                          <code class="small">{{ shortHash(r.transaction_hash) }}</code>
                          <button cButton color="link" size="sm" class="py-0" (click)="copy(r.transaction_hash)">
                            {{ (copied() === r.transaction_hash ? 'mycards.partner.copied' : 'mycards.partner.copy') | translate }}
                          </button>
                        } @else { — }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            @if (rows.length < total) {
              <button cButton color="secondary" variant="outline" size="sm" class="mt-3"
                      [disabled]="listBusy() === section" (click)="loadMore($any(section))">
                @if (listBusy() === section) { <c-spinner size="sm" class="me-1"></c-spinner> }
                {{ 'mycards.partner.load_more' | translate }} ({{ rows.length }} / {{ total }})
              </button>
            }
          }
        </c-card-body>
      </c-card>
    }
  }
}
